import { ChatChannelResponse } from '@/lib/api';
import { ChannelListItem } from './ChannelListItem';

interface ChannelListProps {
  channels: ChatChannelResponse[];
  activeChannelId: string | null;
  onSelectChannel: (channelId: string) => void;
  loading: boolean;
}

export function ChannelList({
  channels,
  activeChannelId,
  onSelectChannel,
  loading,
}: ChannelListProps) {
  if (loading) {
    return (
      <div className="p-4 space-y-3">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="animate-pulse flex items-center gap-3 p-2">
            <div className="h-10 w-10 bg-muted rounded-full" />
            <div className="flex-1">
              <div className="h-4 bg-muted rounded w-24 mb-2" />
              <div className="h-3 bg-muted rounded w-32" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (channels.length === 0) {
    return <div className="p-4 text-center text-sm text-muted-foreground">No channels yet</div>;
  }

  // Group channels by type — guild channels appear under "Community" alongside global
  // Global channels (e.g. Fias Community) always sort first
  const communityChannels = channels
    .filter((c) => c.channelType === 'global' || c.channelType === 'guild')
    .sort((a, b) => (a.channelType === 'global' ? -1 : b.channelType === 'global' ? 1 : 0));
  const directChannels = channels.filter((c) => c.channelType === 'direct');
  const groupChannels = channels.filter((c) => c.channelType === 'group');

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      {communityChannels.length > 0 && (
        <div>
          <div className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Community
          </div>
          {communityChannels.map((channel) => (
            <ChannelListItem
              key={channel.channelId}
              channel={channel}
              isActive={channel.channelId === activeChannelId}
              onClick={() => onSelectChannel(channel.channelId)}
            />
          ))}
        </div>
      )}
      {groupChannels.length > 0 && (
        <div>
          <div className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Groups
          </div>
          {groupChannels.map((channel) => (
            <ChannelListItem
              key={channel.channelId}
              channel={channel}
              isActive={channel.channelId === activeChannelId}
              onClick={() => onSelectChannel(channel.channelId)}
            />
          ))}
        </div>
      )}
      {directChannels.length > 0 && (
        <div>
          <div className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Direct Messages
          </div>
          {directChannels.map((channel) => (
            <ChannelListItem
              key={channel.channelId}
              channel={channel}
              isActive={channel.channelId === activeChannelId}
              onClick={() => onSelectChannel(channel.channelId)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
